import React, { useContext, useEffect, useState } from 'react';
import { CONFIG } from '../config/general';
import { deleteScreenShot, getScreenShots, getUser } from '../services/adminServices';
import { AppContext } from '../context/AppContext';
import { toast } from "react-toastify"
import { checkPermission } from '../helper/helper';
import DropdownWithCheckbox from './Selection';

export const Screenshots = () => {
  const { login } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [users, setUsers] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [fullscreenIndex, setFullscreenIndex] = useState(null);
  const [selectedIds, setSelectedIds] = useState([])
  const [conformationModal, setConformationModal] = useState(false)

  const [filters, setFilters] = useState({
    userId: '',
    startDate: '',
    endDate: '',
  });

  const fetchScreenshots = async (pageNum = 1, append = false) => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: pageNum,
        limit: 10,
        startDate: filters.startDate ? new Date(filters.startDate).toISOString() : '',
        endDate: filters.endDate ? new Date(filters.endDate).toISOString() : '',
      };

      if (filters.selectedUsers?.length > 0) {
        params.userId = filters.selectedUsers.join(',');
      }

      const res = await getScreenShots(login.token, params);

      if (res.success) {
        setScreenshots((prev) => append ? [...prev, ...res.data] : res.data);
        setTotalPages(res.totalPages);
        setPage(res.page);
      }
    } catch (err) {
      console.error('Failed to fetch screenshots:', err.message);
    }
    setLoading(false);
  };

  const getAllUsers = async () => {
    try {
      let result = await getUser(login.token, { name: '' });
      setUsers(result.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    getAllUsers();
    fetchScreenshots(1);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (fullscreenIndex !== null) {
        if (e.key === 'ArrowRight') {
          if (fullscreenIndex < screenshots.length - 1) {
            setFullscreenIndex((prev) => prev + 1);
          } else if (page < totalPages) {
            loadNextPageInFullscreen();
          }
        } else if (e.key === 'ArrowLeft' && fullscreenIndex > 0) {
          setFullscreenIndex((prev) => prev - 1);
        } else if (e.key === 'Escape') {
          setFullscreenIndex(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenIndex, screenshots.length, page, totalPages]);

  const loadNextPageInFullscreen = async () => {
    if (page < totalPages && !loading) {
      const nextPage = page + 1;
      await fetchScreenshots(nextPage, true);
      setFullscreenIndex((prev) => prev + 1);
    }
  };

  const handleApplyFilters = () => {
    setScreenshots([]);
    fetchScreenshots(1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleUserSelection = (userId) => {
    setFilters((prevFilters) => {
      const selected = prevFilters.selectedUsers || [];
      const alreadySelected = selected.includes(userId);
      const updated = alreadySelected
        ? selected.filter((id) => id !== userId)
        : [...selected, userId];
      return { ...prevFilters, selectedUsers: updated };
    });
  };

  const deleteScreenShots = async (Ids = selectedIds) => {
    try {
      if (Ids.length == 0) { return }
      let result = await deleteScreenShot(login.token, { "ss": Ids });
      if (result.success) {
        toast.success(result.message)
        fetchScreenshots(1);
        setConformationModal(false)
        setSelectedIds([])
      } else {
        toast.error(result.message)
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }

  const handleCheckboxChange = (id, isChecked) => {
    setSelectedIds((prev) =>
      isChecked ? [...prev, id] : prev.filter((selectedId) => selectedId !== id)
    );
  };

  return (
    <div className="details-table mt-3">
      {/* Filters */}
      <div className="filters mb-4 p-3 border rounded" style={{ backgroundColor: "var(--card-background)" }}>
        <div className="row g-3 align-items-baseline">
          <div className="col-md-3">
            <label>Name</label>

            {/* ✅ Reusable dropdown with checkboxes */}
            <DropdownWithCheckbox
              options={users} // expects [{ _id, name, label? }]
              selectedItems={filters.selectedUsers || []}
              setSelectedItems={(newSelected) =>
                setFilters({ ...filters, selectedUsers: newSelected })
              }
            />

            {/* ✅ Show selected user names */}
            <div className="mt-2">
              <small>
                {filters.selectedUsers?.length > 0
                  ? `Selected: ${filters.selectedUsers
                    .map((id) => users.find((u) => u._id === id)?.name)
                    .join(", ")}`
                  : "No users selected"}
              </small>
            </div>
          </div>


          <div className="col-md-3">
            <label>Start Date & Time</label>
            <input
              type="datetime-local"
              className="form-control"
              name="startDate"
              value={filters.startDate}
              max={new Date().toISOString().slice(0, 16)}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-3">
            <label>End Date & Time</label>
            <input
              type="datetime-local"
              className="form-control"
              name="endDate"
              value={filters.endDate}
              min={filters.startDate}
              max={new Date().toISOString().slice(0, 16)}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-3 text-end" style={{alignSelf: "center"}}>
            <button className="btn btn-success" onClick={handleApplyFilters} disabled={loading}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          {selectedIds.length > 0 && (
            <div>
              <button className="btn btn-danger" onClick={() => setConformationModal(true)}>Delete</button>
            </div>
          )}
        </div>
        <div>
          <button
            className={`btn me-2 ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('grid')}
          >
            Grid View
          </button>
          <button
            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('list')}
          >
            List View
          </button>
        </div>
      </div>

      {/* Screenshot Display */}
      {loading && page === 1 ? (
        <div className="text-center">
          <img
            src={CONFIG.REACT_APP_UPLOADS + '/Icons/logo.png'}
            alt="Loading"
            style={{ width: '100px' }}
          />
        </div>
      ) : screenshots.length ? (
        viewMode === 'list' ? (
          <>
            <div className="table-responsive">
              <table className="table-hover table-bordered align-middle" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    {checkPermission(login.user, "screenshot:write") && <th style={{ cursor: "pointer" }} onClick={() => { selectedIds.length == screenshots.length ? setSelectedIds([]) : setSelectedIds(screenshots.map(x => { return x._id })) }}>All</th>}
                    <th>#</th>
                    <th>User</th>
                    <th>Preview</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {screenshots.map((ss, index) => (
                    <tr key={ss._id}>
                      {checkPermission(login.user, "screenshot:write") && <td>
                        <div className="form-check d-flex justify-content-center" style={{ fontSize: "20px" }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`checkbox-${ss._id}`}
                            checked={selectedIds.includes(ss._id)}
                            onChange={(e) => handleCheckboxChange(ss._id, e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor={`checkbox-${ss._id}`}></label>
                        </div>
                      </td>}
                      <td>{index + 1}</td>
                      <td>{ss.userId?.name}</td>
                      <td>
                        <img
                          src={`${CONFIG.REACT_APP_UPLOADS}/screenshots/${ss.userId?.username}/${ss.filename}`}
                          alt="Screenshot"
                          style={{ width: 100, cursor: 'pointer' }}
                          onClick={() => setFullscreenIndex(index)}
                        />
                      </td>
                      <td>{new Date(ss.timestamp).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {page < totalPages && (
              <div className="text-center mt-3">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => fetchScreenshots(page + 1, true)}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 g-3">
              {screenshots.map((ss, index) => (
                <div key={ss._id} className="col">
                  <div className="card h-100">
                    <img
                      src={`${CONFIG.REACT_APP_UPLOADS}/screenshots/${ss.userId?.username}/${ss.filename}`}
                      className="card-img-top"
                      alt="Screenshot"
                      style={{ cursor: 'pointer', height: '200px', objectFit: 'cover' }}
                      onClick={() => setFullscreenIndex(index)}
                    />
                    <div className="card-body" style={{ background: "var(--card-background)", color: "var(--black)" }}>
                      <h6 className="card-title">{ss.userId?.name}</h6>
                      <p className="card-text text-muted">
                        {new Date(ss.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {page < totalPages && (
              <div className="text-center mt-3">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => fetchScreenshots(page + 1, true)}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )
      ) : (
        <div className="text-center py-3">No screenshots found.</div>
      )}

      {/* Fullscreen Overlay */}
      {fullscreenIndex !== null && screenshots[fullscreenIndex] && (
        <div
          className="fullscreen-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
          }}
          onClick={() => setFullscreenIndex(null)}
        >
          <img
            src={`${CONFIG.REACT_APP_UPLOADS}/screenshots/${screenshots[fullscreenIndex].userId?.username}/${screenshots[fullscreenIndex].filename}`}
            alt="Full Screenshot"
            style={{
              maxHeight: '90%',
              maxWidth: '90%',
              borderRadius: '8px',
              boxShadow: '0 0 30px rgba(255,255,255,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="btn btn-danger position-absolute top-0 end-0 m-3"
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenIndex(null);
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Modal */}
      <div className={`modal fade ${conformationModal ? "show d-block" : ""}`} tabIndex="-1" role="dialog">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Action</h5>
              <button type="button" className="btn-close" onClick={() => setConformationModal(false)}></button>
            </div>
            <div className="modal-body">Are you sure you want to delete this item?</div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setConformationModal(false)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={() => { deleteScreenShots() }}>Delete</button>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop (for Bootstrap modal behavior) */}
      {conformationModal && <div className="modal-backdrop fade show" onClick={() => setConformationModal(false)}></div>}
    </div>
  );
};
